import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";
import { Save, KeyRound, AlertTriangle, X, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Settings() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    country: user?.country || "", organization: user?.organization || "",
  });
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [showWipe, setShowWipe] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.patch("/auth/profile", form); await refreshMe(); toast.success("Profile updated"); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next !== pwForm.confirm) { setPwError("New passwords don't match."); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) { setPwError(getApiErrorMessage(err)); }
    finally { setPwSaving(false); }
  };

  const resendVerify = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Account" title="Settings" subtitle="Manage your profile, security and account preferences." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Profile" icon={Save}>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <Field label="Full name" name="name" value={form.name} onChange={onChange} testid="setting-name" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="setting-phone" />
              <Field label="Country" name="country" value={form.country} onChange={onChange} testid="setting-country" />
              <Field label="Organization" name="organization" value={form.organization} onChange={onChange} testid="setting-org" />
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={saving} data-testid="save-profile"
                  className="bg-[#2A4D3E] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] disabled:opacity-50 flex items-center gap-1.5">
                  <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </Section>

          <Section title="Change password" icon={KeyRound}>
            <form onSubmit={changePassword} className="space-y-4 max-w-md" data-testid="change-password-form">
              <Field label="Current password" type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} testid="current-password" />
              <div>
                <label className="block text-sm font-medium text-[#5C6660] mb-1.5">New password</label>
                <input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  data-testid="new-password"
                  className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
                <PasswordStrength password={pwForm.next} />
              </div>
              <Field label="Confirm new password" type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} testid="confirm-password" />
              {pwError && <div className="text-sm text-[#C0392B] bg-[#FDEDEC] rounded-xl px-4 py-3" data-testid="pw-error">{pwError}</div>}
              <button type="submit" disabled={pwSaving} data-testid="submit-password-change"
                className="bg-[#2A4D3E] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] disabled:opacity-50">
                {pwSaving ? "Updating…" : "Update password"}
              </button>
            </form>
          </Section>

          {user?.role === "admin" && (
            <Section title="Danger zone" icon={AlertTriangle} accent="#C0392B">
              <p className="text-sm text-[#5C6660] mb-3">Wipe all demo data to prepare AIMS for real production onboarding. This action cannot be undone.</p>
              <button onClick={() => setShowWipe(true)} data-testid="open-wipe-modal"
                className="border border-[#C0392B] text-[#C0392B] rounded-full px-5 py-2 text-sm font-medium hover:bg-[#FDEDEC] flex items-center gap-1.5">
                <AlertTriangle size={14} /> Wipe demo data
              </button>
            </Section>
          )}
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 h-fit">
          <div className="font-heading font-semibold text-lg mb-4 text-[#2A332C]">Account</div>
          <Item label="Email" value={user?.email} />
          <Item label="Role" value={user?.role} />
          <Item label="Email verified" value={user?.email_verified ? "Yes" : "No"}
            action={!user?.email_verified && user?.role !== "patient" && (
              <button onClick={resendVerify} className="text-xs text-[#2A4D3E] font-semibold hover:underline flex items-center gap-1" data-testid="resend-verify-settings">
                <Mail size={12} /> Resend
              </button>
            )} />
          {user?.role === "partner" && (
            <Item label="Commission rate" value={`${((user?.commission_rate || 0) * 100).toFixed(0)}%`} />
          )}
          <Item label="Member since" value={new Date(user?.created_at).toLocaleDateString()} />
        </div>
      </div>

      {showWipe && <WipeModal onClose={() => setShowWipe(false)} onWiped={async () => { await logout(); navigate("/login", { replace: true }); }} />}
    </div>
  );
}

function Section({ title, icon: Icon, accent = "#2A4D3E", children }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
          <Icon size={15} strokeWidth={1.8} />
        </div>
        <div className="font-heading font-semibold text-lg" style={{ color: accent === "#C0392B" ? "#C0392B" : "#2A332C" }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, type = "text", testid, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <input type={type} {...rest} data-testid={testid}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
    </div>
  );
}

function Item({ label, value, action }) {
  return (
    <div className="py-2.5 border-b border-[#E8E6E1] last:border-0 flex items-center justify-between gap-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#8E9992] font-semibold">{label}</div>
        <div className="text-sm text-[#2A332C] capitalize">{value}</div>
      </div>
      {action}
    </div>
  );
}

function WipeModal({ onClose, onWiped }) {
  const [confirm, setConfirm] = useState("");
  const [keep, setKeep] = useState(false);
  const [loading, setLoading] = useState(false);

  const wipe = async () => {
    setLoading(true);
    try {
      await api.post("/admin/wipe-demo-data", { confirm: "WIPE", keep_staff_accounts: keep });
      toast.success("Demo data wiped. Please sign in again.");
      onWiped();
    } catch (e) { toast.error(getApiErrorMessage(e)); setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="wipe-modal">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#FDEDEC] flex items-center justify-center">
              <AlertTriangle size={17} className="text-[#C0392B]" />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold text-[#2A332C]">Wipe all demo data?</div>
              <div className="text-[11px] text-[#8E9992]">This action cannot be undone.</div>
            </div>
          </div>
          <button onClick={onClose}><X size={20} className="text-[#8E9992]" /></button>
        </div>

        <p className="text-sm text-[#5C6660] my-4">
          This will permanently delete all leads, patients, hospitals, doctors, quotations, documents, timeline events,
          commissions and notifications. <strong>Only your admin account will remain.</strong>
        </p>

        <label className="flex items-center gap-2 text-sm text-[#5C6660] mb-4 cursor-pointer">
          <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="keep-staff-checkbox" />
          Keep existing coordinator and partner accounts (rename them later)
        </label>

        <div>
          <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Type <code className="bg-[#F2F4F3] px-1.5 py-0.5 rounded text-[#C0392B] font-semibold">WIPE</code> to confirm</label>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="wipe-confirm-input"
            className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] focus:outline-none focus:border-[#C0392B]" />
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-[#5C6660]">Cancel</button>
          <button onClick={wipe} disabled={confirm !== "WIPE" || loading} data-testid="confirm-wipe"
            className="bg-[#C0392B] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#A03020] disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? "Wiping…" : "Yes, wipe everything"}
          </button>
        </div>
      </div>
    </div>
  );
}
